import { useState, useEffect, useRef, useCallback } from "react";

const SpeechRecognition =
  typeof window !== "undefined"
    ? window.SpeechRecognition || window.webkitSpeechRecognition
    : null;

/**
 * Hook per il riconoscimento vocale tramite Web Speech API del browser.
 *
 * @param {object} options
 * @param {string} [options.lang="it-IT"]
 * @param {function} options.onFinalText - chiamata con il chunk di testo finalizzato
 * @returns {{ isSupported, isListening, interimText, start, stop, toggle }}
 */
export function useSpeechToText({ lang = "it-IT", onFinalText } = {}) {
  const isSupported = Boolean(SpeechRecognition);
  const [isListening, setIsListening] = useState(false);
  const [interimText, setInterimText] = useState("");
  const recognitionRef = useRef(null);
  const onFinalTextRef = useRef(onFinalText);

  // mantieni il ref aggiornato senza riscrivere i handler
  useEffect(() => {
    onFinalTextRef.current = onFinalText;
  }, [onFinalText]);

  const stop = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
  }, []);

  const start = useCallback(() => {
    if (!isSupported) return;

    const recognition = new SpeechRecognition();
    recognition.lang = lang;
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsListening(true);
      setInterimText("");
    };

    recognition.onresult = (event) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          onFinalTextRef.current?.(transcript);
          setInterimText("");
        } else {
          interim += transcript;
        }
      }
      if (interim) setInterimText(interim);
    };

    recognition.onerror = (event) => {
      // "no-speech" è atteso quando l'utente smette di parlare — non mostrare errore
      if (event.error !== "no-speech") {
        console.warn("SpeechRecognition error:", event.error);
      }
      setIsListening(false);
      setInterimText("");
    };

    recognition.onend = () => {
      setIsListening(false);
      setInterimText("");
    };

    recognitionRef.current = recognition;
    recognition.start();
  }, [isSupported, lang]);

  const toggle = useCallback(() => {
    if (isListening) {
      stop();
    } else {
      start();
    }
  }, [isListening, start, stop]);

  // cleanup su unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.onend = null;
        recognitionRef.current.stop();
      }
    };
  }, []);

  return { isSupported, isListening, interimText, start, stop, toggle };
}
